import * as React from "react";
import {
  LayoutDashboard,
  ListOrdered,
  PizzaIcon,
  UserSearch,
  Home,
  Camera,
  Building2,
  Users,
  ChefHat,
  Receipt,
  Activity,
  SlidersHorizontal,
  BookOpen,
} from "lucide-react";

import { NavMain } from "@/components/nav-main";
import { NavUser } from "@/components/nav-user";
import { BranchSwitcher } from "@/components/branch-switcher";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar";
import { usePermissions } from "@/hooks/use-permissions";
import { useLocalization } from "@/context/localization-context";
import { useBackendVersion } from "@/api/backend-version";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { hasPermission, isDeveloper } = usePermissions();
  const { t } = useLocalization();
  const { data: backend } = useBackendVersion();
  const backendVersion = backend?.version || null;

  const data = {
    navMain: [
      ...(hasPermission("dashboard.branch") || hasPermission("dashboard") ? [{
        title: t("nav.dashboard"),
        url: "/dashboard",
        icon: LayoutDashboard,
        items: null,
      }] : []),
      ...(hasPermission("operator.branch") ? [{
        title: t("nav.operator"),
        url: "/operator",
        icon: UserSearch,
        items: null,
      }] : []),
      ...(hasPermission("orders.branch") || hasPermission("orders") ? [{
        title: t("common.orders"),
        url: "/orders",
        icon: ListOrdered,
        items: null,
      }] : []),
      ...(hasPermission("pizzas.branch") || hasPermission("pizzas") ? [{
        title: t("common.pizzas"),
        url: "/pizzas",
        icon: PizzaIcon,
        items: null,
      }] : []),
      ...(hasPermission("pizzas.branch") || hasPermission("pizzas") ? [{
        title: t("nav.pizzaTypes"),
        url: "/pizza-types",
        icon: ChefHat,
        items: null,
      }] : []),
    ],
    navSecondary: [
      {
        title: t("nav.landingPage"),
        url: "/landing",
        icon: Home,
      },
    ],
    organization: [
      ...(hasPermission("billing") ? [{
        title: t("nav.billing"),
        url: "/billing",
        icon: Receipt,
      }] : []),
      ...(hasPermission("branches") ? [{
        title: t("common.branches"),
        url: "/branches",
        icon: Building2,
      },
      {
        title: t("nav.users"),
        url: "/users",
        icon: Users,
      }] : [])
    ],
    developer: [
      ...(isDeveloper ? [{
        title: t("nav.monitoring"),
        url: "/monitoring",
        icon: Activity,
      },
      {
        title: t("nav.evaluationSettings"),
        url: "/evaluation-settings",
        icon: SlidersHorizontal,
      },
      {
        title: t("nav.catalog"),
        url: "/catalog",
        icon: BookOpen,
      }] : []),
    ],
    cameras: [
      ...(hasPermission("cameras") ? [{
        title: t("nav.cameras"),
        url: "/cameras",
        icon: Camera,
      }] : [])
    ]
  };

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <BranchSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={data.navMain} section={t("nav.section.platform")} />
        {data.organization.length > 0 && <NavMain items={data.organization} section={t("nav.section.organization")} />}
        {data.cameras.length > 0 && <NavMain items={data.cameras} section={t("nav.section.devices")} />}
        {data.developer.length > 0 && <NavMain items={data.developer} section={t("nav.section.developer")} />}
        {import.meta.env.DEV && <NavMain items={data.navSecondary} section={t("nav.section.content")} />}
      </SidebarContent>
      <SidebarFooter>
        <div className="flex min-w-0 gap-1 px-2 text-xs text-sidebar-foreground/60 group-data-[collapsible=icon]:hidden">
          <span className="shrink-0" title={t("shell.version.frontend")}>FE v{__APP_VERSION__}</span>
          <span aria-hidden>·</span>
          <span
            className="min-w-0 truncate"
            title={
              backendVersion
                ? t("shell.version.backend", {
                    version: [backendVersion, backend?.git_sha].filter(Boolean).join(" "),
                  })
                : t("shell.version.backendUnknown")
            }
          >
            BE {backendVersion ?? "–"}
          </span>
        </div>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
